import React from 'react';
const MINUTE = 60 * 1000;
/** How often the deployed release is checked. Exported so tests drive the real cadence. */
export const POLL_INTERVAL = 10 * MINUTE;
const reloadWindow = () => window.location.reload();
const alwaysReloadable = () => true;
/**
 * Reads the release identifier the origin is currently serving.
 *
 * @returns the release, or null when it could not be read — a failed poll must not be mistaken
 * for a new release
 */
const fetchAppRelease = () => {
    const releaseURL = `${window.location.origin}/release.txt`;
    return fetch(releaseURL)
        .then(response => (response.ok ? response.text() : null))
        .catch(() => null);
};
/**
 * Reloads the kiosk when a new release is deployed, so a tablet left running picks it up without
 * anyone touching it.
 *
 * The first successful read is only a baseline: it records which release this tablet is already
 * running, so it never triggers a reload itself.
 *
 * @param options - see {@link UseReloadOnNewReleaseOptions}
 */
export const useReloadOnNewRelease = ({ canReload = alwaysReloadable, reload = reloadWindow, } = {}) => {
    const baselineRef = React.useRef(null);
    // The callbacks are read through refs so the polling effect can stay keyed to mount: the
    // interval must outlive every re-render, not just the first fetch resolving.
    const canReloadRef = React.useRef(canReload);
    const reloadRef = React.useRef(reload);
    React.useEffect(() => {
        canReloadRef.current = canReload;
        reloadRef.current = reload;
    }, [canReload, reload]);
    React.useEffect(() => {
        let cancelled = false;
        const checkRelease = () => {
            fetchAppRelease().then(release => {
                if (cancelled || release === null) {
                    return;
                }
                if (baselineRef.current === null) {
                    baselineRef.current = release;
                    return;
                }
                if (baselineRef.current !== release && canReloadRef.current()) {
                    reloadRef.current();
                }
            });
        };
        checkRelease();
        const interval = setInterval(checkRelease, POLL_INTERVAL);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, []);
};
